import React, { useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, CalendarClock, CheckCircle2, Landmark, LockKeyhole } from 'lucide-react';
import { apiErrorMessage, completeContractAccount } from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { Chip, Table } from '../amendments/AmendmentGroupPane';
import { Button, Card, CardHead, INK, fieldBase } from '../onboarding/review/reviewStyles';
import type {
  CollectionPeriodPreview,
  CollectionPeriodRecord,
  ContractAccountRecord,
  ContractAccountSnapshot,
  FacilityContractVersion,
  PaymentCalendarSnapshot,
} from '../types/contractWorkspace';
import type { SourceRequest } from './ContractSourcePanel';

/**
 * Accounts and the payment calendar are contract terms (credit agreement Sections 1.01 and 7).
 * They are extracted with the document, completed by a maker where the executed copy is silent,
 * and go live only when a checker activates the version. The Collections tab and every
 * collection period run from them.
 *
 * On the review standard (Slice 6, Decision 1.5 revised) and, since the walkthrough finding
 * F-S6-016, reading the schedules *in force* for the version: a version that predates
 * carry-forward holds no rows of its own, so the panel shows the nearest in-force version's
 * rows labelled with where they come from, and the facility's actual collection periods —
 * never an inference that "no periods exist" from the version's own rows.
 */

const GroupHead = styled.h3`
  display: flex; align-items: center; gap: 7px; margin: 16px 0 6px; font-size: 14px; color: ${INK.strong};
  &:first-of-type { margin-top: 4px; }
`;
const Chips = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 6px;`;
const Entry = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr) auto; gap: 8px; margin-top: 8px; align-items: start;
  input { ${fieldBase}; border-color: #7c6420; }
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;
const Periods = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); gap: 8px; margin: 6px 0 4px;
`;
const Period = styled.div`
  padding: 10px 12px; border: 1px solid ${INK.line}; border-radius: 9px; background: ${INK.raised}; display: grid; gap: 3px;
  .m { display: flex; justify-content: space-between; align-items: center; gap: 8px; color: ${INK.strong}; font-weight: 650; font-size: 13px; }
  .r { display: flex; justify-content: space-between; gap: 8px; color: ${INK.muted}; font-size: 12px; }
  .r span:last-child { color: ${INK.body}; font-variant-numeric: tabular-nums; }
  .note { color: #edcc82; font-size: 12px; line-height: 1.35; margin-top: 2px; }
`;
const Empty = styled.p`color: ${INK.muted}; font-size: 13px;`;

const TYPE_LABEL: Record<string, string> = {
  COLLECTION: 'Collection account', LOCKBOX: 'Lockbox account', RESERVE: 'Reserve account',
  PAYMENT: 'Payment account', DISTRIBUTION: 'Distribution account', OTHER: 'Account',
};
const fmtDate = (value?: string | null, withDay = false) => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', withDay ? { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};
const ordinal = (day: number) => {
  const suffix = day % 100 >= 11 && day % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] || 'th';
  return `${day}${suffix}`;
};
const conventionText = (value?: string | null) => value === 'MODIFIED_FOLLOWING' ? 'modified following business day'
  : value === 'PRECEDING' ? 'preceding business day' : 'following business day';
const dueText = (value?: string | null) => value === 'DETERMINATION_DATE' ? 'Due on each determination date' : value ? value.replace(/_/g, ' ').toLowerCase() : 'Not stated';
const bbcText = (value?: string | null) => value === 'MONTHLY_AND_EACH_BORROWING' ? 'Monthly, and with each borrowing request'
  : value ? value.replace(/_/g, ' ').toLowerCase() : 'Not stated';
const monthLabel = (label: string) => {
  const [year, month] = label.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};
const periodTone = (status: string): StatusTone => ['SIGNED_OFF', 'CLOSED'].includes(status) ? 'done'
  : status === 'RESTATING' ? 'warning' : status === 'OPEN' ? 'progress' : 'neutral';
const periodWord = (status: string) => status.replace(/_/g, ' ').toLowerCase();
const clause = (value?: string | null) => String(value || '').replace(/^Credit Agreement, /, '');

interface Props {
  version: FacilityContractVersion;
  canComplete: boolean;
  currentUser: string;
  onChanged: () => Promise<void> | void;
  onFeedback: (feedback: ActionFeedbackState) => void;
  // Opens the structured amendment with the accounts and calendar editor, so a
  // facility without a machine-readable agreement is never stuck (2x2 rule).
  onAddSchedules?: () => void;
  /** F-MK-084: renders a SOURCE cell that opens the source panel at the row's document and page. */
  renderSource?: (request: SourceRequest) => React.ReactNode;
}

const CollectionsSchedulesPanel: React.FC<Props> = ({ version, canComplete, currentUser, onChanged, onFeedback, onAddSchedules, renderSource }) => {
  const configuration = version.contractConfiguration || {};
  const extracted: ContractAccountSnapshot[] = configuration.accounts || [];
  const statedCalendar = (configuration.paymentCalendar && 'determinationDay' in configuration.paymentCalendar
    ? configuration.paymentCalendar : null) as PaymentCalendarSnapshot | null;
  const inForce = version.schedulesInForce;
  const ownLive = (version.contractAccounts || []).length > 0 || Boolean(version.paymentCalendar);
  const liveAccounts = version.contractAccounts?.length ? version.contractAccounts : inForce?.accounts || [];
  const liveCalendar = version.paymentCalendar || inForce?.paymentCalendar || null;
  const live = ownLive || liveAccounts.length > 0 || Boolean(liveCalendar);
  const periods: CollectionPeriodRecord[] = inForce?.collectionPeriods || [];
  const unresolved = (configuration.unresolved || []).filter(item => item.startsWith('accounts') || item === 'paymentCalendar');
  const preview: CollectionPeriodPreview[] = version.collectionPeriodPreview || [];
  const [drafts, setDrafts] = useState<Record<string, { number: string; source: string }>>({});
  const [busy, setBusy] = useState('');

  if (!extracted.length && !statedCalendar && !live) {
    // Nothing stated on this version and nothing in force for it. Say what the periods say,
    // not what the absence of rows might mean.
    return <Card>
      <CardHead>
        <div><h2>Accounts and payment calendar</h2>
          <p>This version states no accounts or payment calendar and none is in force from an earlier version.
            {periods.length ? ` ${periods.length} collection period(s) exist on the facility.` : ' No collection periods exist on the facility.'} Enter them from the agreement or the account bank's documents; a different checker activates them.</p></div>
        <CalendarClock size={18} color={INK.faint} />
      </CardHead>
      {onAddSchedules
        ? <Button $variant="primary" type="button" onClick={onAddSchedules}><Landmark size={14} /> Add accounts and payment calendar</Button>
        : <Empty>{version.state === 'ACTIVE' ? 'A portfolio, trading, operations or administrator maker adds them by amendment.' : 'Add them when this version is active, by amendment.'}</Empty>}
    </Card>;
  }

  const needsEntry = extracted.filter(account => !account.maskedAccountNumber);
  const completedByMe = extracted.some(account => account.completion?.completedBy
    && account.completion.completedBy.toLowerCase() === currentUser.toLowerCase());

  const record = async (account: ContractAccountSnapshot) => {
    const draft = drafts[account.accountKey] || { number: '', source: '' };
    setBusy(account.accountKey);
    try {
      await completeContractAccount(version.id, { accountKey: account.accountKey, accountNumber: draft.number, sourceReference: draft.source });
      onFeedback({ tone: 'success', message: `${account.accountName || TYPE_LABEL[account.accountType]} recorded as ****${draft.number.replace(/\D/g, '').slice(-4)}. A different checker activates the version.` });
      setDrafts(current => ({ ...current, [account.accountKey]: { number: '', source: '' } }));
      await onChanged();
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The account number could not be recorded.') });
    } finally { setBusy(''); }
  };

  // The live row rides along (F-MK-084) so its source can be traced to the version it was first read from.
  const accountRows: Array<ContractAccountSnapshot & { record?: ContractAccountRecord }> = live && liveAccounts.length
    ? liveAccounts.map(row => ({
      accountKey: row.account_key, accountType: row.account_type, accountName: row.account_name,
      bankName: row.bank_name || '', maskedAccountNumber: row.masked_account_number, controlAgreement: row.control_agreement,
      sweepToAccountKey: row.sweep_to_account_key, sweepFrequency: row.sweep_frequency,
      requiredBalancePct: row.required_balance_pct, sourceClause: row.source_clause || '', sourcePage: row.source_page, sourceSection: row.source_section,
      completion: (row.completion_json && typeof row.completion_json === 'object' ? row.completion_json : null) as ContractAccountSnapshot['completion'],
      record: row,
    }))
    : extracted;
  const nameOf = (key?: string | null) => accountRows.find(item => item.accountKey === key)?.accountName || key || '';
  const accountsCarried = !version.contractAccounts?.length && inForce?.accountsCarriedFromVersionNo != null ? inForce.accountsCarriedFromVersionNo : null;
  const calendarCarried = !version.paymentCalendar && inForce?.calendarCarriedFromVersionNo != null ? inForce.calendarCarriedFromVersionNo : null;

  const calendarView: PaymentCalendarSnapshot | null = statedCalendar || (liveCalendar ? {
    collectionPeriod: liveCalendar.collection_period, firstPeriodStart: liveCalendar.first_period_start,
    determinationDay: liveCalendar.determination_day, paymentDay: liveCalendar.payment_day,
    businessDayConvention: liveCalendar.business_day_convention, holidayCalendar: liveCalendar.holiday_calendar,
    servicerReportDue: liveCalendar.servicer_report_due, loanTapeDue: liveCalendar.loan_tape_due,
    bbcFrequency: liveCalendar.bbc_frequency, sourceClause: liveCalendar.source_clause,
    sourcePage: liveCalendar.source_page, sourceSection: liveCalendar.source_section,
  } as PaymentCalendarSnapshot : null);

  return <Card>
    <CardHead>
      <div><h2>Accounts and payment calendar</h2>
        <p>Contract terms from the agreement's definitions and accounts sections, extracted from the document or entered by a maker. They go live when a checker activates the version; the Collections tab and every collection period run from them.</p></div>
      {live && onAddSchedules ? <Button type="button" onClick={onAddSchedules}>Change by amendment</Button> : <Landmark size={18} color={INK.accent} />}
    </CardHead>
    <Chips>
      {live ? <Chip $colour="#7cddbe"><CheckCircle2 size={11} style={{ marginRight: 4 }} />Live since activation</Chip> : <Chip $colour="#a6d8f5">Extracted · goes live at activation</Chip>}
      <Chip $colour="#a8b6ca">{accountRows.length} account{accountRows.length === 1 ? '' : 's'}{accountsCarried != null ? ` · carried from version ${accountsCarried}` : ''}</Chip>
      {needsEntry.length > 0 && !live && <Chip $colour="#edcc82"><AlertTriangle size={11} style={{ marginRight: 4 }} />{needsEntry.length} needs entry</Chip>}
      {calendarView ? <Chip $colour="#a8b6ca">Payment calendar{calendarCarried != null ? ` · carried from version ${calendarCarried}` : ''}</Chip> : <Chip $colour="#edcc82">No payment calendar</Chip>}
      {unresolved.includes('paymentCalendar') && <Chip $colour="#edcc82">Calendar unreadable in the document</Chip>}
      {completedByMe && !live && <Chip $colour="#edcc82"><LockKeyhole size={11} style={{ marginRight: 4 }} />You completed an account, so another checker activates</Chip>}
    </Chips>

    <GroupHead><Landmark size={14} color={INK.muted} />Accounts</GroupHead>
    {accountRows.length === 0 ? <Empty>No accounts are stated on this version or in force from an earlier one.</Empty> : <Table>
      <thead><tr><th>Account</th><th>Bank and number</th><th>Role</th><th>Source</th><th>State</th></tr></thead>
      <tbody>{accountRows.map(account => {
        const draft = drafts[account.accountKey] || { number: '', source: '' };
        const missing = !account.maskedAccountNumber;
        return <tr key={account.accountKey} data-carried={accountsCarried != null ? 'true' : undefined}>
          <td><strong>{account.accountName || TYPE_LABEL[account.accountType] || account.accountType}</strong><div style={{ color: INK.muted, fontSize: 12 }}>{TYPE_LABEL[account.accountType] || account.accountType}</div></td>
          <td>
            <div>{account.bankName}</div>
            <div style={{ fontVariantNumeric: 'tabular-nums' }}>{account.maskedAccountNumber || <span style={{ color: '#edcc82' }}>Number not in the executed copy</span>}</div>
            {missing && !live && (canComplete
              ? <Entry>
                <input id={`contract-account-${account.accountKey}`} aria-label={`Account number for ${account.accountName || account.accountKey}`} placeholder="Account number" inputMode="numeric" value={draft.number} onChange={e => setDrafts({ ...drafts, [account.accountKey]: { ...draft, number: e.target.value } })} />
                <input id={`contract-account-source-${account.accountKey}`} aria-label="Source of the account number" placeholder="Source, e.g. paying agent letter of Sep 3" value={draft.source} onChange={e => setDrafts({ ...drafts, [account.accountKey]: { ...draft, source: e.target.value } })} />
                <Button $variant="primary" type="button" disabled={busy !== '' || draft.number.replace(/\D/g, '').length < 4 || !draft.source.trim()}
                  title={draft.number.replace(/\D/g, '').length < 4 ? 'Enter at least the last four digits.' : !draft.source.trim() ? 'Name the document the number comes from.' : undefined}
                  onClick={() => void record(account)}>Record number</Button>
              </Entry>
              : <div style={{ color: INK.muted, marginTop: 4 }}>A maker records the number from the paying agent's documentation.</div>)}
            {account.completion && (account.completion.completedBy || account.completion.sourceReference) && <div style={{ color: INK.muted, marginTop: 4 }}>{[account.completion.completedBy ? `Entered by ${account.completion.completedBy}` : null, account.completion.sourceReference || null].filter(Boolean).join(' · ')}</div>}
          </td>
          <td className="muted">
            {account.controlAgreement || '—'}
            {account.sweepToAccountKey && <div>Sweeps {account.sweepFrequency ? account.sweepFrequency.toLowerCase() : ''} to the {nameOf(account.sweepToAccountKey).toLowerCase()}</div>}
            {account.requiredBalancePct != null && <div>Required balance {Number(account.requiredBalancePct).toFixed(2)}% of the receivables balance</div>}
          </td>
          <td className="source">{renderSource ? renderSource({
            uid: `account:${account.accountKey}`, table: 'contractAccounts', row: (account.record as unknown as Record<string, unknown>) || null,
            kind: 'ACCOUNT', section: 'accounts', label: account.accountName || TYPE_LABEL[account.accountType] || account.accountType, group: 'Accounts',
            value: [account.bankName, account.maskedAccountNumber].filter(Boolean).join(' · ') || 'Number not in the executed copy',
            detail: account.controlAgreement || null, page: account.sourcePage, sectionRef: account.sourceSection, clause: clause(account.sourceClause),
          }) : <>{clause(account.sourceClause)}{account.sourcePage ? ` · page ${account.sourcePage}` : ''}</>}</td>
          <td>{live ? <StatusChip tone="done" label={accountsCarried != null ? `Live · version ${accountsCarried}` : 'Live'} /> : missing ? <StatusChip tone="warning" label="Needs entry" /> : <StatusChip tone="progress" label="Extracted" />}</td>
        </tr>;
      })}</tbody>
    </Table>}

    <GroupHead><CalendarClock size={14} color={INK.muted} />Payment calendar</GroupHead>
    {calendarView ? <Table>
      <tbody>
        <tr><td><strong>Collection period</strong></td><td>Each calendar month; the first begins {fmtDate(calendarView.firstPeriodStart)}</td><td className="source" rowSpan={6}>{renderSource ? renderSource({
          // The live row, when there is one, records which version first stated the calendar; a stated
          // calendar copied into an amendment's configuration would otherwise open the amendment's PDF.
          uid: 'calendar', table: 'paymentCalendar', row: (liveCalendar as unknown as Record<string, unknown>) || null,
          kind: 'CALENDAR', section: 'calendar', label: 'Payment calendar', group: 'Payment calendar',
          value: `Determination ${ordinal(calendarView.determinationDay)}, payment ${ordinal(calendarView.paymentDay)} of each month`,
          detail: `${conventionText(calendarView.businessDayConvention)} · first period begins ${fmtDate(calendarView.firstPeriodStart)}`,
          page: calendarView.sourcePage, sectionRef: calendarView.sourceSection, clause: clause(calendarView.sourceClause),
        }) : clause(calendarView.sourceClause)}{calendarCarried != null ? <div style={{ marginTop: 4 }}>Carried from version {calendarCarried}</div> : null}</td></tr>
        <tr><td><strong>Determination date</strong></td><td>{ordinal(calendarView.determinationDay)} of each month, {conventionText(calendarView.businessDayConvention)}</td></tr>
        <tr><td><strong>Payment date</strong></td><td>{ordinal(calendarView.paymentDay)} of each month, {conventionText(calendarView.businessDayConvention)}</td></tr>
        <tr><td><strong>Business days</strong></td><td>{calendarView.holidayCalendar === 'US_FEDERAL' ? 'U.S. federal holiday calendar (New York banks)' : calendarView.holidayCalendar}</td></tr>
        <tr><td><strong>Servicer report and tape</strong></td><td>{dueText(calendarView.servicerReportDue)}</td></tr>
        <tr><td><strong>Borrowing base certificate</strong></td><td>{bbcText(calendarView.bbcFrequency)}</td></tr>
      </tbody>
    </Table> : <Empty>No payment calendar is stated on this version or in force from an earlier one.</Empty>}

    {periods.length > 0 ? <>
      <GroupHead><CalendarClock size={14} color={INK.muted} />Collection periods on the facility</GroupHead>
      <Periods>{periods.map(period => <Period key={period.id}>
        <span className="m">{monthLabel(period.period_label)}<StatusChip tone={periodTone(period.status)} label={periodWord(period.status)} /></span>
        <div className="r"><span>Period</span><span>{fmtDate(period.period_start).replace(/, \d{4}$/, '')} – {fmtDate(period.period_end).replace(/, \d{4}$/, '')}</span></div>
        <div className="r"><span>Determination</span><span>{fmtDate(period.determination_date, true).replace(/, \d{4}$/, '')}</span></div>
        <div className="r"><span>Payment</span><span>{fmtDate(period.payment_date, true).replace(/, \d{4}$/, '')}</span></div>
      </Period>)}</Periods>
    </> : preview.length > 0 && <>
      <GroupHead><CalendarClock size={14} color={INK.muted} />{live ? 'First collection periods' : 'Collection periods this calendar creates'}</GroupHead>
      <Periods>{preview.map(period => <Period key={period.periodLabel}>
        <span className="m">{monthLabel(period.periodLabel)}</span>
        <div className="r"><span>Period</span><span>{fmtDate(period.periodStart).replace(/, \d{4}$/, '')} – {fmtDate(period.periodEnd).replace(/, \d{4}$/, '')}</span></div>
        <div className="r"><span>Determination</span><span>{fmtDate(period.determinationDate, true).replace(/, \d{4}$/, '')}</span></div>
        <div className="r"><span>Payment</span><span>{fmtDate(period.paymentDate, true).replace(/, \d{4}$/, '')}</span></div>
        {period.rollNotes.length > 0 && <div className="note">{period.rollNotes.join('; ')}.</div>}
      </Period>)}</Periods>
    </>}
  </Card>;
};

export default CollectionsSchedulesPanel;
